import { Button } from "@natera/form";
import React, { FC, useContext, useEffect, useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  NotificationContext,
  ServiceContext,
  TestResultContext,
  UserContext,
} from "@app/provider";
import { routes } from "@app/routing";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { TestDetailsContext } from "@app/provider/testData";
import { IconButton } from "@natera/material/lib/button";
import { Notification } from "@app/components";
import { generatePassword } from "@app/utils";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { HEAP_EVENTS } from "@app/provider/types";
import { saveAs } from "file-saver";

import CopySvg from "@assets/svg/icons/copy.svg";

import "./shareFetalSex.scss";

const messages = defineMessages({
  shareFetalSexHeader: {
    id: "shareFetalSexHeader",
    defaultMessage: "Share Fetal Sex",
  },
  shareFetalSexTitle: {
    id: "shareFetalSexTitle",
    defaultMessage: "Share Fetal Sex",
  },
  shareFetalSexSubTitle: {
    id: "shareFetalSexSubTitle",
    defaultMessage:
      "You can share the fetal sex with friends and family with these easy steps:",
  },
  shareFetalSexStep1: {
    id: "shareFetalSexStep1",
    defaultMessage:
      'Click the "Download PDF" button. You’ll get a password-protected PDF with your fetal sex results.',
  },
  shareFetalSexStep2: {
    id: "shareFetalSexStep2",
    defaultMessage:
      "Attach the PDF to a text message or email. <b>If you’re asked for a password, don’t enter one — entering it opens the PDF and shows you the fetal sex results.</b>",
  },
  shareFetalSexStep3: {
    id: "shareFetalSexStep3",
    defaultMessage: "Click the copy icon next to the password on this screen.",
  },
  shareFetalSexStep4: {
    id: "shareFetalSexStep4",
    defaultMessage:
      "Paste the password into the message with the PDF attached.",
  },
  shareFetalSexPassword: {
    id: "shareFetalSexPassword",
    defaultMessage: "Password",
  },
  shareFetalSexCopyPasswordBtnAriaLabel: {
    id: "shareFetalSexCopyPasswordBtnAriaLabel",
    defaultMessage: "Copy password",
  },
  shareFetalSexDownloadPdfBtn: {
    id: "shareFetalSexDownloadPdfBtn",
    defaultMessage: "Download PDF",
  },
  shareFetalSexPasswordCopied: {
    id: "shareFetalSexPasswordCopied",
    defaultMessage: "Password copied to your clipboard.",
  },
  shareFetalSexNote: {
    id: "shareFetalSexNote",
    defaultMessage:
      "A new password is generated every time you open this screen. Please make sure you share the correct password.",
  },
  shareFetalSexFooter: {
    id: "shareFetalSexFooter",
    defaultMessage:
      "By sharing this document and password with others, you understand that the personal health information contained in the document may no longer be protected by HIPAA or other applicable privacy or confidentiality laws. Please exercise caution when sharing as Natera is not responsible for your further sharing of this document.",
  },
});

const ShareFetalSexPage: FC = () => {
  const intl = useIntl();
  const { getTestDetails, getOrderUidPublic, getTestUid } =
    useContext(TestDetailsContext);
  const { testResult, getTestResult, getTestResultError } =
    useContext(TestResultContext);
  const { fileService } = useContext(ServiceContext);
  const { isLoading } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const testCard = getTestDetails();
  const testUid = getTestUid();
  const orderUidPublic = getOrderUidPublic();

  // The password must stay stable across re-renders, otherwise the value the user
  // copied stops matching the one the downloaded PDF was encrypted with.
  const generatedPassword = useMemo(() => generatePassword(), []);

  useEffect(() => {
    if (orderUidPublic && testUid && !getTestResultError) {
      getTestResult(orderUidPublic, testUid);
    }
  }, []);

  if (!testResult || !testCard) {
    return null;
  }

  const downloadPdf = async () => {
    const body = { password: generatedPassword };

    const fileData = await fileService.retrieveProtectedFile(
      `/result/fetal-sex-pdf/${orderUidPublic}`,
      body
    );

    saveAs(fileData, `fetal-sex-report.pdf`);

    heap.track(HEAP_EVENTS.upp_fetalsexreporting_downloadcta, {
      lims_clinic_id: testCard.clinic?.limsId,
      public_order_uid: testCard.orderUidPublic,
    });
  };

  const copyPassword = async () => {
    await navigator.clipboard.writeText(generatedPassword);

    addNotification({
      message: intl.formatMessage(messages.shareFetalSexPasswordCopied),
      type: "success",
      hasTimeout: true,
    });

    heap.track(HEAP_EVENTS.upp_fetalsexreporting_copy, {
      lims_clinic_id: testCard.clinic?.limsId,
      public_order_uid: testCard.orderUidPublic,
    });
  };

  return (
    <BlueHeaderContent
      blueHeaderTitle={intl.formatMessage(messages.shareFetalSexHeader)}
      backArrowHeaderRoute={routes.testResultDetailsPage(
        testCard.orderUidPublic,
        testCard.uid
      )}
      fullScreen
    >
      <LoadingContext isLoading={isLoading}>
        <div
          className="share-fetal-sex_page"
          data-testid="share-fetal-sex-page"
        >
          <section className="share-fetal-sex_card">
            <div className="share-fetal-sex_card-header">
              <h2 className="share-fetal-sex_test-title">
                {intl.formatMessage(messages.shareFetalSexTitle)}
              </h2>
              <h3 className="share-fetal-sex_sub-title">
                {intl.formatMessage(messages.shareFetalSexSubTitle)}
              </h3>
              <ol className="share-fetal-sex_steps">
                <li>{intl.formatMessage(messages.shareFetalSexStep1)}</li>
                <li>
                  {intl.formatMessage(messages.shareFetalSexStep2, {
                    b: (...chunks) => <b>{chunks}</b>,
                  })}
                </li>
                <li>{intl.formatMessage(messages.shareFetalSexStep3)}</li>
                <li>{intl.formatMessage(messages.shareFetalSexStep4)}</li>
              </ol>
            </div>
            <div className="share-fetal-sex_card-main">
              <div className="password">
                <span className="password_label">{`${intl.formatMessage(
                  messages.shareFetalSexPassword
                )}:`}</span>
                <span className="password_content">{generatedPassword}</span>
                <IconButton
                  className="copy-password-btn"
                  onClick={copyPassword}
                  aria-label={intl.formatMessage(
                    messages.shareFetalSexCopyPasswordBtnAriaLabel
                  )}
                >
                  {CopySvg}
                </IconButton>
              </div>
              <Button onClick={downloadPdf} raised>
                {intl.formatMessage(messages.shareFetalSexDownloadPdfBtn)}
              </Button>

              <div className="information-container">
                <Notification className="password-warning" type="error">
                  {intl.formatMessage(messages.shareFetalSexNote)}
                </Notification>

                <div className="disclaimer">
                  {intl.formatMessage(messages.shareFetalSexFooter)}
                </div>
              </div>
            </div>
          </section>
        </div>
      </LoadingContext>
    </BlueHeaderContent>
  );
};
export default ShareFetalSexPage;
